import type { LoggerConfig } from '../config/logger.config.js';
import { describeLogData } from './log-safe.js';
import { LogLevel } from '../config/logger.config.js';

// Browser-safe env access — `process` is a Node global and is undeclared
// in browser contexts, so a bare `process.env.NODE_ENV` throws
// `ReferenceError: process is not defined` when this module is imported
// directly into a browser (e.g., a Vite/Webpack project that doesn't
// pre-bundle deps from node_modules, or `<script type=module>` consumers).
// Bundlers like esbuild/Vite still statically substitute `process.env.X`
// inside the conditional at build time; in unbundled environments we
// fall back to an empty object so reads are `undefined`, never throw.
const _ENV: Record<string, string | undefined> =
  typeof process !== 'undefined' && process.env ? process.env : {};

// Build-time constants for dead code elimination
const IS_PRODUCTION = _ENV.NODE_ENV === 'production';
const IS_DEVELOPMENT = _ENV.NODE_ENV === 'development';
const IS_CI = _ENV.CI === 'true';
const LIONRAPID_DEV = _ENV.LIONRAPID_DEV === 'true';
const PERFORMANCE_TRACKING = _ENV.LIONRAPID_PERFORMANCE_TRACKING === 'true';

/**
 * Read a runtime log-level override from localStorage in browser contexts.
 * Lets a developer flip on verbose logging without a rebuild via
 *   localStorage.LIONRAPID_LOG_LEVEL = 'DEBUG'
 * Returns null on SSR or restrictive sandboxes.
 */
function readBrowserLogLevel(): string | null {
  try {
    if (typeof localStorage !== 'undefined') {
      const v = localStorage.getItem('LIONRAPID_LOG_LEVEL');
      if (v && typeof v === 'string') return v;
    }
  } catch {
    // localStorage unavailable (privacy mode, file://, etc.) — ignore
  }
  return null;
}

// Environment-specific configuration.
// Default is WARN — quiet by default. Customers consuming the published
// ESM bundles via npm don't get noisy debug/info chatter on every page load.
// Override via:
//   - build-time: process.env.LOG_LEVEL=DEBUG
//   - runtime (browser): localStorage.LIONRAPID_LOG_LEVEL = 'DEBUG'
//   - runtime (programmatic): logger.configure({ level: LogLevel.DEBUG })
const LOG_LEVEL_ENV = readBrowserLogLevel() || _ENV.LOG_LEVEL || 'WARN';

/**
 * ANSI color codes for terminal output
 */
export const Colors = {
  // Reset
  RESET: '\x1b[0m',
  // Foreground colors
  BLACK: '\x1b[30m',
  RED: '\x1b[31m',
  GREEN: '\x1b[32m',
  YELLOW: '\x1b[33m',
  BLUE: '\x1b[34m',
  MAGENTA: '\x1b[35m',
  CYAN: '\x1b[36m',
  WHITE: '\x1b[37m',
  ORANGE: '\x1b[38;5;208m',
  // Bright foreground colors
  BRIGHT_BLACK: '\x1b[90m',
  BRIGHT_RED: '\x1b[91m',
  BRIGHT_GREEN: '\x1b[92m',
  BRIGHT_YELLOW: '\x1b[93m',
  BRIGHT_BLUE: '\x1b[94m',
  BRIGHT_MAGENTA: '\x1b[95m',
  BRIGHT_CYAN: '\x1b[96m',
  BRIGHT_WHITE: '\x1b[97m',
  // Styles
  BOLD: '\x1b[1m',
  DIM: '\x1b[2m',
  UNDERLINE: '\x1b[4m',
} as const;

export interface CallSite {
  fileName: string;
  lineNumber: number;
  functionName?: string;
}

export interface PackageLoggerConfig extends LoggerConfig {
  packageName?: string;
  packageEmoji?: string;
}

// Development logger class - completely eliminated in production
class DevelopmentLogger {
  private config: Required<LoggerConfig>;

  constructor(config: PackageLoggerConfig = {}) {
    this.config = {
      level: this.getLogLevelFromEnv(),
      enabled: this.shouldEnableLogging(),
      prefix: `${config.packageEmoji || '📦'} ${config.packageName || 'LionRapid'}`,
      showLineNumbers: LIONRAPID_DEV && !IS_CI,
      useColors: this.shouldUseColors(),
      ...config,
    };
  }

  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  debug(message: string, data?: unknown): void {
    if (!this.config.enabled || this.config.level > LogLevel.DEBUG) return;
    this.log(LogLevel.DEBUG, message, data);
  }

  info(message: string, data?: unknown): void {
    if (this.config.level > LogLevel.INFO) return;
    this.log(LogLevel.INFO, message, data);
  }

  warn(message: string, data?: unknown): void {
    this.log(LogLevel.WARN, message, data);
  }

  error(message: string, data?: unknown): void {
    this.log(LogLevel.ERROR, message, data);
  }

  time(label: string): () => void {
    if (!PERFORMANCE_TRACKING || IS_PRODUCTION) return () => {};

    const start = IS_DEVELOPMENT ? performance.now() : 0;
    return () => {
      if (IS_DEVELOPMENT) {
        const duration = performance.now() - start;
        this.debug(`⏱️ ${label}: ${duration.toFixed(2)}ms`, { duration });
      }
    };
  }

  private log(level: LogLevel, message: string, data?: unknown): void {
    if (!this.config.enabled || level < this.config.level) return;

    const levelName = LogLevel[level];
    const levelEmoji = this.getLevelEmoji(level);
    const callSite = this.config.showLineNumbers ? this.getCallSite() : null;

    const logMessage = this.buildColoredMessage(
      level,
      levelEmoji,
      levelName,
      message,
      callSite,
      data
    );

    switch (level) {
      case LogLevel.ERROR:
        console.error(logMessage);
        break;
      case LogLevel.WARN:
        console.warn(logMessage);
        break;
      case LogLevel.INFO:
        console.info(logMessage);
        break;
      default:
        console.log(logMessage);
    }
  }

  private buildColoredMessage(
    level: LogLevel,
    emoji: string,
    levelName: string,
    message: string,
    callSite: CallSite | null,
    data?: unknown
  ): string {
    if (!this.config.useColors) {
      const locationInfo = callSite
        ? ` (${callSite.fileName}:${callSite.lineNumber})`
        : '';
      let logMessage = `${this.config.prefix} ${emoji} ${levelName} ${message}${locationInfo}`;
      if (data !== undefined) {
        logMessage += ` ${describeLogData(data)}`;
      }
      return logMessage;
    }

    const levelColor = this.getLevelColor(level);
    const reset = Colors.RESET;

    const coloredPrefix = `${Colors.BOLD}${Colors.ORANGE}${this.config.prefix}${reset}`;
    const coloredEmoji = emoji;
    const coloredLevel = `${levelColor}${Colors.BOLD}${levelName}${reset}`;
    const coloredMessage = `${levelColor}${message}${reset}`;

    const locationInfo = callSite
      ? `${Colors.DIM}${Colors.BRIGHT_BLACK} (${callSite.fileName}:${callSite.lineNumber})${reset}`
      : '';

    const dataInfo =
      data !== undefined
        ? `${Colors.DIM}${Colors.CYAN} ${describeLogData(data)}${reset}`
        : '';

    return `${coloredPrefix} ${coloredEmoji} ${coloredLevel} ${coloredMessage}${locationInfo}${dataInfo}`;
  }

  private getLevelColor(level: LogLevel): string {
    switch (level) {
      case LogLevel.DEBUG:
        return Colors.BRIGHT_GREEN;
      case LogLevel.INFO:
        return Colors.BRIGHT_CYAN;
      case LogLevel.WARN:
        return Colors.BRIGHT_YELLOW;
      case LogLevel.ERROR:
        return Colors.BRIGHT_RED;
      default:
        return Colors.BRIGHT_BLACK;
    }
  }

  private getLevelEmoji(level: LogLevel): string {
    switch (level) {
      case LogLevel.DEBUG:
        return '🔍';
      case LogLevel.INFO:
        return 'ℹ️';
      case LogLevel.WARN:
        return '⚠️';
      case LogLevel.ERROR:
        return '❌';
      default:
        return '📝';
    }
  }

  private getCallSite(): CallSite | null {
    try {
      const stack = new Error().stack;
      if (!stack) return null;

      const lines = stack.split('\n');
      for (let i = 3; i < lines.length; i++) {
        const line = lines[i];
        if (line && !line.includes('logger.ts') && !line.includes('Logger')) {
          return this.parseStackLine(line);
        }
      }
    } catch {
      // Ignore errors
    }
    return null;
  }

  private parseStackLine(line: string): CallSite | null {
    let match = line.match(/at\s+(.+?)\s+\((.+):(\d+):(\d+)\)/);
    if (match) {
      return {
        functionName: match[1],
        fileName: this.extractFileName(match[2]),
        lineNumber: parseInt(match[3], 10),
      };
    }

    match = line.match(/at\s+(.+):(\d+):(\d+)/);
    if (match) {
      return {
        fileName: this.extractFileName(match[1]),
        lineNumber: parseInt(match[2], 10),
      };
    }

    return null;
  }

  private extractFileName(filePath: string): string {
    const parts = filePath.split('/');
    return parts[parts.length - 1] || filePath;
  }

  colorize(text: string, color: keyof typeof Colors): string {
    if (!this.config.useColors) return text;
    return `${Colors[color]}${text}${Colors.RESET}`;
  }

  getColors(): typeof Colors {
    return Colors;
  }

  createPackageLogger(
    packageName: string,
    packageEmoji?: string
  ): DevelopmentLogger {
    return new DevelopmentLogger({
      ...this.config,
      packageName,
      packageEmoji: packageEmoji || '📦',
    });
  }

  private getLogLevelFromEnv(): LogLevel {
    switch (LOG_LEVEL_ENV.toUpperCase()) {
      case 'DEBUG':
        return LogLevel.DEBUG;
      case 'INFO':
        return LogLevel.INFO;
      case 'WARN':
        return LogLevel.WARN;
      case 'ERROR':
        return LogLevel.ERROR;
      case 'NONE':
        return LogLevel.NONE;
      default:
        // Unknown level → quiet fallback (WARN).
        return LogLevel.WARN;
    }
  }

  private shouldEnableLogging(): boolean {
    if (LOG_LEVEL_ENV === 'NONE') return false;
    return true;
  }

  private shouldUseColors(): boolean {
    if (IS_CI) return false;
    // Check if we're in a browser environment
    if (typeof window !== 'undefined') {
      return true; // Browsers support console colors
    }
    // Node.js environment
    if (
      typeof process !== 'undefined' &&
      process.stdout &&
      !process.stdout.isTTY
    ) {
      return false;
    }
    return IS_DEVELOPMENT;
  }
}

// Production no-op functions - these create completely eliminatable calls
const noop = () => {};
const noopWithReturn = () => () => {};

// Export logger with build-time method elimination
export const logger = IS_PRODUCTION
  ? {
      configure: noop,
      debug: noop, // ← This eliminates the call completely
      info: noop, // ← This eliminates the call completely
      warn: (message: string, data?: unknown) => {
        console.warn(`🦁 LionRapid Core ⚠️ ${message}`, data || '');
      },
      error: (message: string, data?: unknown) => {
        console.error(`🦁 LionRapid Core ❌ ${message}`, data || '');
      },
      time: noopWithReturn,
      colorize: (text: string) => text,
      getColors: () => Colors,
      createPackageLogger: (packageName: string, packageEmoji?: string) => ({
        configure: noop,
        debug: noop, // ← Package logger debug also eliminated
        info: noop, // ← Package logger info also eliminated
        warn: (message: string, data?: unknown) => {
          const prefix = `${packageEmoji || '📦'} ${packageName}`;
          console.warn(`${prefix} ⚠️ ${message}`, data || '');
        },
        error: (message: string, data?: unknown) => {
          const prefix = `${packageEmoji || '📦'} ${packageName}`;
          console.error(`${prefix} ❌ ${message}`, data || '');
        },
        time: noopWithReturn,
        colorize: (text: string) => text,
        getColors: () => Colors,
        createPackageLogger: noop,
      }),
    }
  : new DevelopmentLogger({
      packageName: 'LionRapid Core',
      packageEmoji: '🦁',
    });

// Export Logger class for type compatibility
export const Logger = IS_PRODUCTION
  ? function Logger() {
      return logger;
    }
  : DevelopmentLogger;

// Factory function with build-time elimination
export const createPackageLogger = (config: PackageLoggerConfig) => {
  if (IS_PRODUCTION) {
    const prefix = `${config.packageEmoji || '📦'} ${config.packageName || 'LionRapid'}`;
    return {
      configure: noop,
      debug: noop, // ← All debug calls eliminated
      info: noop, // ← All info calls eliminated
      warn: (message: string, data?: unknown) => {
        console.warn(`${prefix} ⚠️ ${message}`, data || '');
      },
      error: (message: string, data?: unknown) => {
        console.error(`${prefix} ❌ ${message}`, data || '');
      },
      time: noopWithReturn,
      colorize: (text: string) => text,
      getColors: () => Colors,
      createPackageLogger: noop,
    };
  }
  return new DevelopmentLogger(config);
};

// Performance measurement helper - eliminated in production
export const measure = (name: string) => {
  if (IS_PRODUCTION) {
    return {
      start: noop,
      end: noop,
      elapsed: () => 0,
    };
  }

  let startTime: number;
  return {
    start: () => {
      if (IS_DEVELOPMENT) {
        startTime = performance.now();
        logger.debug(`⏱️ ${name} - started`);
      }
    },
    end: () => {
      if (IS_DEVELOPMENT) {
        const duration = performance.now() - startTime;
        logger.debug(`⏱️ ${name} - completed in ${duration.toFixed(2)}ms`);
        return duration;
      }
      return 0;
    },
    elapsed: () => (IS_DEVELOPMENT ? performance.now() - startTime : 0),
  };
};
